import { useState, useMemo, Fragment, useEffect, useCallback, useRef, memo } from "react";
import { Card, CardContent } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  getAgenticAiLogs,
  getAgenticAiLogStats,
  exportAgenticAiLogs,
  AiLogResponse,
  AiLogStats,
  summarizeAiEvents,
} from "./Api/agenticAiLogsApi";
import { formatAgentDisplayName } from "./agentDisplayName";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../../components/StorageHelper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

import {
  Search,
  Bot,
  Globe,
  FileText,
  FilePen,
  Trash2,
  Terminal,
  RefreshCw,
  ChevronRight,
  Clock,
  Download,
  Sparkles,
  Loader2,
  Network,
  ActivitySquare,
  User,
  Monitor,
  Filter,
} from "lucide-react";
import { Device } from "../interfaces/EndpointInterfaces";
import CloudEntityActivityLogs from "../CloudAnalytics/CloudEntityActivityLogs";

interface AgenticActivityProps {
  device?: Device;
  global?: boolean;
}

type EventType =
  | "process-launch"
  | "network-connect"
  | "file-read"
  | "file-write"
  | "file-delete";

interface AgentEvent {
  id: string;
  timestamp: string;
  type: EventType;
  rawUser: string;
  computer?: string;
  parentProcess: string;
  commandLine?: string;
  destination?: string;
  destinationIp?: string;
  port?: number;
  childProcess?: string;
  filePath?: string;
  action?: string;
  message?: string;
  rawEvent?: any;
  eventJson?: string | object;
}
interface UiBurstGroup {
  burstId: string;
  burstStartTime: string;
  burstEndTime: string;
  eventCount: number;
  events: AgentEvent[];
}

function parseRawUser(raw: string): { agent: string | null; user: string } {
  const normalized = raw.trim();
  const dashIndex = normalized.indexOf("-");

  if (dashIndex > 0) {
    const agent = normalized.slice(0, dashIndex).trim().toLowerCase();
    return {
      agent: agent || null,
      user: dashIndex >= normalized.length - 1 ? "unknown" : normalized.slice(dashIndex + 1).trim(),
    };
  }

  if (!normalized) {
    return { agent: null, user: raw };
  }

  return {
    agent: normalized.toLowerCase(),
    user: "unknown",
  };
}

const PALETTE = [
  { badgeCls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border border-orange-200 dark:border-orange-700", cardRing: "border-orange-200 dark:border-orange-800", iconBg: "bg-orange-100 dark:bg-orange-900/50", iconText: "text-orange-600 dark:text-orange-400", dotCls: "bg-orange-500" },
  { badgeCls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border border-sky-200 dark:border-sky-700", cardRing: "border-sky-200 dark:border-sky-800", iconBg: "bg-sky-100 dark:bg-sky-900/50", iconText: "text-sky-600 dark:text-sky-400", dotCls: "bg-sky-500" },
  { badgeCls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-violet-200 dark:border-violet-700", cardRing: "border-violet-200 dark:border-violet-800", iconBg: "bg-violet-100 dark:bg-violet-900/50", iconText: "text-violet-600 dark:text-violet-400", dotCls: "bg-violet-500" },
  { badgeCls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border border-teal-200 dark:border-teal-700", cardRing: "border-teal-200 dark:border-teal-800", iconBg: "bg-teal-100 dark:bg-teal-900/50", iconText: "text-teal-600 dark:text-teal-400", dotCls: "bg-teal-500" },
  { badgeCls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700", cardRing: "border-blue-200 dark:border-blue-800", iconBg: "bg-blue-100 dark:bg-blue-900/50", iconText: "text-blue-600 dark:text-blue-400", dotCls: "bg-blue-500" },
  { badgeCls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700", cardRing: "border-emerald-200 dark:border-emerald-800", iconBg: "bg-emerald-100 dark:bg-emerald-900/50", iconText: "text-emerald-600 dark:text-emerald-400", dotCls: "bg-emerald-500" },
  { badgeCls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-700", cardRing: "border-rose-200 dark:border-rose-800", iconBg: "bg-rose-100 dark:bg-rose-900/50", iconText: "text-rose-600 dark:text-rose-400", dotCls: "bg-rose-500" },
];

function hashAgentKey(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % PALETTE.length;
  }
  return hash;
}

function getAgentStyle(agentKey: string) {
  return PALETTE[hashAgentKey(agentKey)] ?? PALETTE[0];
}



function AgentBadge({ agent }: { agent: string | null }) {
  if (!agent) return <span className="text-xs text-muted-foreground">—</span>;
  const style = getAgentStyle(agent);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${style.badgeCls}`}>
      <Bot className="w-3 h-3" />
      {formatAgentDisplayName(agent)}
    </span>
  );
}

const EVENT_TYPE_META: Record<EventType, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  "process-launch": { label: "Launch", icon: Terminal, color: "text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700" },
  "network-connect": { label: "Network", icon: Globe, color: "text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700" },
  "file-read": { label: "Read", icon: FileText, color: "text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700" },
  "file-write": { label: "Write", icon: FilePen, color: "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700" },
  "file-delete": { label: "Delete", icon: Trash2, color: "text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700" },
};

function EventTypePill({ type }: { type: EventType }) {
  const { label, icon: Icon, color } = EVENT_TYPE_META[type];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${color}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function portToProto(port: number | undefined): string {
  if (port === 443) return "https";
  if (port === 80) return "http";
  if (port === 22) return "ssh";
  if (port === 21) return "ftp";
  if (port === 25 || port === 587) return "smtp";
  if (port === 3389) return "rdp";
  return port != null ? String(port) : "";
}

function groupByBurst(events: AgentEvent[], gapSecs = 300): AgentEvent[][] {
  if (events.length === 0) return [];
  const groups: AgentEvent[][] = [[events[0]]];
  for (let i = 1; i < events.length; i++) {
    const prev = new Date(events[i - 1].timestamp).getTime();
    const curr = new Date(events[i].timestamp).getTime();
    if (prev - curr <= gapSecs * 1000) {
      groups[groups.length - 1].push(events[i]);
    } else {
      groups.push([events[i]]);
    }
  }
  return groups;
}

function sortEventsDescending(events: AgentEvent[]): AgentEvent[] {
  return [...events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

function sortBurstsDescending(bursts: UiBurstGroup[]): UiBurstGroup[] {
  return [...bursts].sort((a, b) => new Date(b.burstStartTime).getTime() - new Date(a.burstStartTime).getTime());
}

function eventSummary(e: AgentEvent): string {
  if (e.type === "process-launch") {
    return e.commandLine ?? `${e.parentProcess.split("\\").pop()} launched ${e.childProcess ?? "subprocess"}`;
  }
  if (e.type === "network-connect") {
    const proc = e.parentProcess.split("\\").pop() ?? e.parentProcess;
    return `${proc} → ${e.destination}`;
  }
  const proc = e.parentProcess.split("\\").pop() ?? e.parentProcess;
  const file = e.filePath?.split("\\").pop() ?? e.filePath ?? "";
  const op = e.type === "file-read" ? "read" : e.type === "file-write" ? "wrote" : "deleted";
  return `${proc} ${op} ${file}`;
}

function rawSensorLine(e: AgentEvent): string {
  if (e.type === "process-launch") {
    return `Process: ${e.parentProcess} launched application: ${e.commandLine} for user: ${e.rawUser}`;
  }
  if (e.type === "network-connect") {
    return `Process: ${e.parentProcess} connecting to: ${e.destination} (${e.destinationIp}:${e.port}) for user: ${e.rawUser}`;
  }
  const op = e.type === "file-read" ? "read" : e.type === "file-write" ? "wrote" : "deleted";
  return `Process: ${e.parentProcess} ${op}: ${e.filePath} for user: ${e.rawUser}`;
}



function parseEventJsonPayload(value: unknown): any {
  if (value == null) return undefined;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  return typeof value === "object" ? value : undefined;
}

function findNestedCommandLine(payload: any): string | undefined {
  if (payload == null) return undefined;
  if (typeof payload === "string") return undefined;
  if (Array.isArray(payload)) {
    for (const item of payload) {
      const nested = findNestedCommandLine(item);
      if (nested) return nested;
    }
    return undefined;
  }

  for (const key of Object.keys(payload)) {
    const value = payload[key];
    const normalizedKey = key.toLowerCase();
    if (["commandline", "command", "cmd", "command_line", "args", "arguments"].includes(normalizedKey) && typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (typeof value === "object") {
      const nested = findNestedCommandLine(value);
      if (nested) return nested;
    }
  }

  return undefined;
}

function getProcessLaunchCommand(event: AgentEvent): string | undefined {

  const payload = parseEventJsonPayload(event.eventJson ?? event.rawEvent);
  return findNestedCommandLine(payload);
}

function getEventPort(event: AgentEvent): number | undefined {
  const eventJson =
    typeof event.eventJson === "string"
      ? parseEventJsonPayload(event.eventJson)
      : event.eventJson;

  const port =
    eventJson?.RemotePort ??
    eventJson?.remotePort;

  if (
    port === undefined ||
    port === null ||
    port === ""
  ) {
    return undefined;
  }

  const parsedPort = Number(port);

  return Number.isNaN(parsedPort)
    ? undefined
    : parsedPort;
}

function displayEventMessage(event: AgentEvent): string | undefined {
  if (event.type === "process-launch") {
    return getProcessLaunchCommand(event) || event.message || event.action;
  }
  return event.message || event.action;
}

function formatEventJson(value: string | object): string {
  if (typeof value !== "string") {
    return JSON.stringify(value, null, 2);
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function formatTs(ts: string) {
  const d = new Date(ts);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}
function formatTime(ts: string) {
  const d = new Date(ts);
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}


type TimeWindow = "lw" | "lm" | "l3m" | "ly" | "custom";

const GROUPS_BATCH_SIZE = 8;

function EventCell({ event }: { event: AgentEvent }) {
  if (event.type === "process-launch") {
    const psCmd = displayEventMessage(event) ?? "";

    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-mono text-xs text-foreground line-clamp-2 break-all leading-relaxed">{psCmd || "Command unavailable"}</span>
      </div>
    );
  }

  if (event.type === "network-connect") {
    let port = getEventPort(event);
    const proto = portToProto(port);
    const protoBadgeColor =
      proto === "https" ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700" :
        proto === "http" ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700" :
          proto === "ssh" ? "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-700" :
            "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-mono text-xs text-foreground line-clamp-2 break-all leading-relaxed">{event.destination || event.action || event.message || "Destination unavailable"}</span>

        </div>
        {
          proto && (
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border flex-shrink-0 ${protoBadgeColor}`}>{proto}</span>
          )
        }
      </div>
    );
  }

  if (event.type === "file-read" || event.type === "file-write" || event.type === "file-delete") {
    return (
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-mono text-xs text-foreground line-clamp-2 break-all leading-relaxed">{displayEventMessage(event) || event.filePath || "File event"}</span>
      </div>
    );
  }

  return <span className="font-mono text-xs text-foreground">Unsupported event</span>;
}




const EventListItem = memo(function EventListItem({ event }: { event: AgentEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div
        className="flex items-center gap-2.5 py-2 px-3 cursor-pointer hover-elevate"
        onClick={() => setOpen(!open)}
      >
        <EventTypePill type={event.type} />
        <div className="flex-1 min-w-0">
          <EventCell event={event} />
        </div>
        <span className="text-[11px] font-mono text-muted-foreground flex-shrink-0 tabular-nums">{formatTime(event.timestamp)}</span>
        <ChevronRight className={`w-3 h-3 text-muted-foreground flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
      </div>
      {open && (
        <div className="mx-3 mb-2 p-2.5 rounded-md bg-muted/50 border border-border">

          {(event.type === "process-launch" || event.type === "network-connect") && (
            <>
              <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">
                Message
              </p>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-mono text-xs text-foreground line-clamp-2 break-all leading-relaxed">
                  {
                    event.message ||
                    "Destination unavailable"}
                </span>
              </div>
            </>
          )}

          {(event.eventJson || event.rawEvent) && (
            <div className="mt-3">
              <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">
                Event JSON
              </p>

              {(() => {
                let parsed: any = {};

                try {
                  const raw = event.eventJson ?? event.rawEvent;

                  parsed =
                    raw && typeof raw === "string"
                      ? JSON.parse(raw)
                      : raw || {};
                } catch (e) {
                  parsed = {};
                }

                const isEmpty =
                  !parsed ||
                  (typeof parsed === "object" &&
                    Object.keys(parsed).length === 0);

                return isEmpty ? (
                  <span className="text-sm text-muted-foreground">
                    No data available.
                  </span>
                ) : (
                  <CloudEntityActivityLogs data={parsed} />
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function BurstCard({ group, isLast, global: isGlobal }: { group: AgentEvent[]; isLast: boolean; global?: boolean }) {
  const SHOW_MAX = 6;
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const agents = [...new Set(group.map((e) => parseRawUser(e.rawUser).agent).filter(Boolean))] as string[];
  const users = [...new Set(group.map((e) => parseRawUser(e.rawUser).user))];
  const computers = [...new Set(group.map((e) => e.computer).filter(Boolean))] as string[];
  const launchCount = group.filter((e) => e.type === "process-launch").length;
  const networkCount = group.filter((e) => e.type === "network-connect").length;
  const fileCount = group.filter((e) => e.type.startsWith("file-")).length;
  const start = formatTs(group[group.length - 1].timestamp);
  const end = formatTs(group[0].timestamp);

  async function handleAiSummarize() {
    setLoading(true);
    setAiError(null);
    try {
      const eventMessages = group.map((e) => rawSensorLine(e));

      const summary = await summarizeAiEvents(eventMessages);

      if (summary) {
        setAiSummary(summary);
      } else {
        setAiError("No summary was generated.");
      }
    } catch (err: any) {
      console.error("Failed to summarize burst:", err);
      setAiError(err.message || "Failed to summarize. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex gap-0 group/burst">
      <div className="flex flex-col items-center w-9 flex-shrink-0 pt-3.5">
        <div className="w-2.5 h-2.5 rounded-full bg-background border-2 border-foreground/30 flex-shrink-0 z-10" />
        {!isLast && <div className="flex-1 w-px bg-border mt-1" />}
      </div>

      <Card className="flex-1 mb-4 overflow-visible">
        <CardContent className="p-4 space-y-3">

          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-semibold text-foreground tabular-nums">
              {start === end ? start : <>{start}<span className="font-normal text-muted-foreground"> – {end}</span></>}
            </span>
            <div className="w-px h-3 bg-border flex-shrink-0" />
            {agents.map((a) => <AgentBadge key={a} agent={a} />)}
            {users.map((u) => (
              <span key={u} className="text-[11px] text-muted-foreground flex items-center gap-1">
                <User className="w-2.5 h-2.5" />{u}
              </span>
            ))}
            {isGlobal && computers.map((c) => (
              <span key={c} className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Monitor className="w-2.5 h-2.5" />{c}
              </span>
            ))}
            <div className="flex-1" />
            {launchCount > 0 && <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 px-1.5 py-0.5 rounded whitespace-nowrap">{launchCount} launch{launchCount > 1 ? "es" : ""}</span>}
            {networkCount > 0 && <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 px-1.5 py-0.5 rounded whitespace-nowrap">{networkCount} network</span>}
            {fileCount > 0 && <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 px-1.5 py-0.5 rounded whitespace-nowrap">{fileCount} file</span>}
          </div>

          <div className="space-y-1.5">
            {aiSummary && <p className="text-sm leading-relaxed text-foreground">{aiSummary}</p>}
            {aiError && <p className="text-xs text-red-600 dark:text-red-400">{aiError}</p>}
            <button
              onClick={handleAiSummarize}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiSummary ? "Re-summarize with AI" : "Summarize with AI"}
            </button>
          </div>

          <div className="space-y-1.5">
            {expanded && (
              <div className="border border-border rounded-md overflow-hidden divide-y divide-border">
                {(showAll ? group : group.slice(0, SHOW_MAX)).map((e) => (
                  <EventListItem key={e.id} event={e} />
                ))}
                {!showAll && group.length > SHOW_MAX && (
                  <button
                    onClick={() => setShowAll(true)}
                    className="w-full py-2 px-3 text-xs text-muted-foreground hover:text-foreground transition-colors text-left hover-elevate"
                  >
                    +{group.length - SHOW_MAX} more event{group.length - SHOW_MAX !== 1 ? "s" : ""}…
                  </button>
                )}
              </div>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
              {expanded ? "Collapse events" : `Show ${group.length} event${group.length !== 1 ? "s" : ""}`}
            </button>
          </div>

        </CardContent>
      </Card>
    </div>
  );
}


const MAX_MINUTES = 30 * 24 * 60;

const ActivitySearchInput = memo(function ActivitySearchInput({
  onSubmit,
  onClear,
  loading,
}: {
  onSubmit: (query: string) => void;
  onClear: () => void;
  loading: boolean;
}) {
  const [draft, setDraft] = useState("");
  const hasAppliedRef = useRef(false);

  const handleChange = (value: string) => {
    setDraft(value);
    if (value.length === 0 && hasAppliedRef.current) {
      hasAppliedRef.current = false;
      onClear();
    }
  };

  const handleSubmit = () => {
    const query = draft.trim();
    if (query) {
      hasAppliedRef.current = true;
      onSubmit(query);
      return;
    }
    if (hasAppliedRef.current) {
      hasAppliedRef.current = false;
      onClear();
    }
  };

  return (
    <div className="relative flex-1 min-w-48 flex gap-2">
      <Input
        placeholder="Search events, commands, domains…"
        value={draft}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
        className="h-9 text-sm"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9 flex-shrink-0"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
});

const GlobalSummaryPanel = memo(function GlobalSummaryPanel({ bursts }: { bursts: UiBurstGroup[] }) {
  const allEvents = useMemo(() => bursts.flatMap(b => b.events), [bursts]);
  const latest = useMemo(() => {
    if (!allEvents.length) {
      return new Date();
    }

    const timestamps = allEvents
      .map((e) => new Date(e.timestamp).getTime())
      .filter((t) => !isNaN(t));

    if (!timestamps.length) {
      return new Date();
    }

    return new Date(Math.max(...timestamps));
  }, [allEvents]);

  const [hours, setHours] = useState("24");
  const [minutes, setMinutes] = useState("0");
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalMins = useMemo(() => {
    const raw = (parseInt(hours, 10) || 0) * 60 + (parseInt(minutes, 10) || 0);
    return Math.min(Math.max(raw, 1), MAX_MINUTES);
  }, [hours, minutes]);

  const windowEvents = useMemo(() => {
    const cutoff = latest.getTime() - totalMins * 60_000;
    return allEvents.filter((e) => new Date(e.timestamp).getTime() >= cutoff);
  }, [allEvents, latest, totalMins]);

  const winLabel = totalMins >= 60
    ? `last ${Math.floor(totalMins / 60)}h${totalMins % 60 > 0 ? ` ${totalMins % 60}m` : ""}`
    : `last ${totalMins}m`;

  function clampAndSet(val: string, setter: (v: string) => void, maxVal: number) {
    const n = parseInt(val, 10);
    if (isNaN(n) || val === "") { setter(""); setSummary(null); return; }
    setter(String(Math.min(Math.max(n, 0), maxVal)));
    setSummary(null);
  }

  async function handleSummarize() {
    try {
      setLoading(true);
      setError(null);
      setSummary(null);

      if (windowEvents.length === 0) {
        setError("No events found in selected time range");
        return;
      }
      const messages = windowEvents.map((event) => {
        return (
          event.message ||
          event.action ||
          "Unknown activity"
        );
      });

      const response = await summarizeAiEvents(messages);

      if (!response?.trim()) {
        setError("No summary generated");
        return;
      }

      setSummary(response);
    } catch (err: any) {
      console.error(err);

      setError(
        err?.message ||
        "Failed to generate summary"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-semibold text-foreground">Session Summary</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Last</span>
                <input
                  type="number"
                  min={0}
                  max={720}
                  value={hours}
                  onChange={(e) => clampAndSet(e.target.value, setHours, 720)}
                  className="w-14 text-xs bg-background border border-border rounded-md px-2 py-1 text-foreground text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-xs text-muted-foreground">hr</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={minutes}
                  onChange={(e) => clampAndSet(e.target.value, setMinutes, 59)}
                  className="w-14 text-xs bg-background border border-border rounded-md px-2 py-1 text-foreground text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-xs text-muted-foreground">min</span>
                <span className="text-xs text-muted-foreground">·</span>
                <span className="text-xs text-muted-foreground">{windowEvents.length} event{windowEvents.length !== 1 ? "s" : ""}</span>
              </div>
              <Button size="sm" variant="outline" onClick={handleSummarize} disabled={loading || windowEvents.length === 0} className="ml-auto">
                {loading
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Analyzing…</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Summarize</>}
              </Button>
            </div>
            {!summary && !loading && !error && (
              <p className="text-xs text-muted-foreground mt-2">
                Enter a look-back window (max 1 month) and summarize all AI agent activity in that period.
              </p>
            )}
            {loading && (
              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                Analyzing {windowEvents.length} events in the {winLabel}…
              </div>
            )}
            {error && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{error}</p>}
            {summary && !loading && (
              <div className="mt-2.5">
                <p className="text-sm text-foreground leading-relaxed">{summary}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    <Sparkles className="w-3 h-3 mr-1" />AI-generated · {winLabel} · {windowEvents.length} events
                  </Badge>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

const BurstCardFromApi = memo(function BurstCardFromApi({ burst, isLast, global: isGlobal }: { burst: UiBurstGroup; isLast: boolean; global?: boolean }) {
  const SHOW_MAX = 6;
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const agents = [...new Set(burst.events.map((e) => parseRawUser(e.rawUser).agent).filter(Boolean))] as string[];
  const users = [...new Set(burst.events.map((e) => parseRawUser(e.rawUser).user))];
  const computers = [...new Set(burst.events.map((e) => e.computer).filter(Boolean))] as string[];
  const launchCount = burst.events.filter((e) => e.type === "process-launch").length;
  const networkCount = burst.events.filter((e) => e.type === "network-connect").length;
  const fileCount = burst.events.filter((e) => e.type.startsWith("file-")).length;
  const start = formatTs(burst.burstStartTime);
  const end = formatTs(burst.burstEndTime);

  async function handleAiSummarize() {
    try {
      setLoading(true);
      setAiError(null);
      setAiSummary(null);

      if (!burst.events || burst.events.length === 0) {
        setAiError("No events available for summarization");
        return;
      }

      const eventMessages = burst.events.map((event) => {
        return (
          event.message ||
          "Unknown activity"
        );
      });

      const summary = await summarizeAiEvents(eventMessages);

      if (!summary?.trim()) {
        setAiError("No summary generated");
        return;
      }

      setAiSummary(summary);
    } catch (err: any) {
      console.error("AI summarization failed:", err);

      setAiError(
        err?.message ||
        "Failed to generate AI summary"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex gap-0 group/burst">
      <div className="flex flex-col items-center w-9 flex-shrink-0 pt-3.5">
        <div className="w-2.5 h-2.5 rounded-full bg-background border-2 border-foreground/30 flex-shrink-0 z-10" />
        {!isLast && <div className="flex-1 w-px bg-border mt-1" />}
      </div>

      <Card className="flex-1 mb-4 overflow-visible">
        <CardContent className="p-4 space-y-3">

          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-semibold text-foreground tabular-nums">
              {start === end ? start : <>{start}<span className="font-normal text-muted-foreground"> – {end}</span></>}
            </span>
            <div className="w-px h-3 bg-border flex-shrink-0" />
            {agents.map((a) => <AgentBadge key={a} agent={a} />)}
            {users.map((u) => (
              <span key={u} className="text-[11px] text-muted-foreground flex items-center gap-1">
                <User className="w-2.5 h-2.5" />{u}
              </span>
            ))}
            {isGlobal && computers.map((c) => (
              <span key={c} className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Monitor className="w-2.5 h-2.5" />{c}
              </span>
            ))}
            <div className="flex-1" />
            {launchCount > 0 && <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 px-1.5 py-0.5 rounded whitespace-nowrap">{launchCount} launch{launchCount > 1 ? "es" : ""}</span>}
            {networkCount > 0 && <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 px-1.5 py-0.5 rounded whitespace-nowrap">{networkCount} network</span>}
            {fileCount > 0 && <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 px-1.5 py-0.5 rounded whitespace-nowrap">{fileCount} file</span>}
          </div>

          <div className="space-y-1.5">
            {aiSummary && <p className="text-sm leading-relaxed text-foreground">{aiSummary}</p>}
            {aiError && <p className="text-xs text-red-600 dark:text-red-400">{aiError}</p>}
            <button
              onClick={handleAiSummarize}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiSummary ? "Re-summarize with AI" : "Summarize with AI"}
            </button>
          </div>

          <div className="space-y-1.5">
            {expanded && (
              <div className="border border-border rounded-md overflow-hidden divide-y divide-border">
                {(showAll ? burst.events : burst.events.slice(0, SHOW_MAX)).map((e) => (
                  <EventListItem key={e.id} event={e} />
                ))}
                {!showAll && burst.events.length > SHOW_MAX && (
                  <button
                    onClick={() => setShowAll(true)}
                    className="w-full py-2 px-3 text-xs text-muted-foreground hover:text-foreground transition-colors text-left hover-elevate"
                  >
                    +{burst.events.length - SHOW_MAX} more event{burst.events.length - SHOW_MAX !== 1 ? "s" : ""}…
                  </button>
                )}
              </div>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
              {expanded ? "Collapse events" : `Show ${burst.events.length} event${burst.events.length !== 1 ? "s" : ""}`}
            </button>
          </div>

        </CardContent>
      </Card>
    </div>
  );
});

const ActivityBurstList = memo(function ActivityBurstList({
  bursts,
  isGlobal,
  aiLogsLoading,
  aiLogsLoadingMore,
  hasMore,
  sentinelRef,
}: {
  bursts: UiBurstGroup[];
  isGlobal: boolean;
  aiLogsLoading: boolean;
  aiLogsLoadingMore: boolean;
  hasMore: boolean;
  sentinelRef: React.RefObject<HTMLDivElement>;
}) {
  const totalEvents = useMemo(
    () => bursts.reduce((sum, b) => sum + b.events.length, 0),
    [bursts]
  );

  if (bursts.length === 0) {
    return (
      <>
        <div className="text-center py-16 text-muted-foreground text-sm flex flex-col items-center gap-3">
          {aiLogsLoading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400" />
              <span>Loading AI activity…</span>
            </>
          ) : (
            "No events match your filters"
          )}
        </div>
        <p className="text-xs text-muted-foreground">0 bursts with 0 total events</p>
      </>
    );
  }

  return (
    <>
      <div className={`pl-2 relative ${aiLogsLoading ? "opacity-60 pointer-events-none" : ""}`}>
        {aiLogsLoading && (
          <div className="absolute inset-0 flex items-start justify-center pt-8 z-10">
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 bg-background/80 border border-border rounded-md px-3 py-2 shadow-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Applying filters…</span>
            </div>
          </div>
        )}
        {bursts.map((burst, index) => (
          <BurstCardFromApi
            key={burst.burstId}
            burst={burst}
            isLast={index === bursts.length - 1}
            global={isGlobal}
          />
        ))}
        {hasMore && (
          <div ref={sentinelRef} className="mt-3 h-10 flex items-center justify-center text-xs text-muted-foreground">
            {aiLogsLoadingMore ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading more activity…
              </div>
            ) : (
              "Scroll to load more activity…"
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {bursts.length} burst{bursts.length !== 1 ? "s" : ""} with {totalEvents} total events
        {hasMore && " (more available)"}
      </p>
    </>
  );
});

export default function AgenticActivity({ device, global: isGlobal = false }: AgenticActivityProps) {
  const userData = useUserStore((state) => state.user);
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

  const [bursts, setBursts] = useState<UiBurstGroup[]>([]);
  const [aiLogsLoading, setAiLogsLoading] = useState(false);
  const [aiLogsLoadingMore, setAiLogsLoadingMore] = useState(false);
  const [aiLogsError, setAiLogsError] = useState<string | null>(null);
  const [lastAiLogsRefresh, setLastAiLogsRefresh] = useState<Date | null>(null);
  const [overviewStats, setOverviewStats] = useState<AiLogStats | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const [typeFilter, setTypeFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeWindow>("lw");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [jsonFilter, setJsonFilter] = useState("");
  const committedSearchRef = useRef("");

  const handleSelectedTimeRangeChange = (value: string) => {
    setSelectedTimeRange(value as TimeWindow);
  };
  const [showJsonFilter, setShowJsonFilter] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchAgenticAiLogs = useCallback(async (
    cursor: string | null = null,
    append = false,
    options: { suppressLoading?: boolean; fetchStats?: boolean; searchQuery?: string } = {}
  ) => {
    if (!userData?.email || !latestTenant) {
      console.warn("Missing user email or tenant name for AI logs fetch");
      return;
    }

    if (append) {
      setAiLogsLoadingMore(true);
    } else {
      setAiLogsLoading(true);
      setHasMore(true);
    }
    setAiLogsError(null);

    try {
      const sourceSearch = options.searchQuery !== undefined
        ? options.searchQuery
        : committedSearchRef.current;
      const searchQuery = [sourceSearch, agentFilter !== "all" ? agentFilter : undefined]
        .filter(Boolean)
        .join(" ");

      const queryOptions = {
        computerName: device?.name,
        computerId: device?.id,
        cursor: cursor || undefined,
        limit: GROUPS_BATCH_SIZE,
        searchQuery: searchQuery || undefined,
        typeFilter: typeFilter === "all" ? undefined : typeFilter,
        agentFilter: agentFilter === "all" ? undefined : agentFilter,
        jsonFilter: jsonFilter || undefined,
        customStartDate: selectedTimeRange === "custom" ? customStartDate : undefined,
        customEndDate: selectedTimeRange === "custom" ? customEndDate : undefined,
      };

      const response: AiLogResponse | undefined = await getAgenticAiLogs(
        userData.email,
        latestTenant,
        selectedTimeRange,
        isGlobal,
        queryOptions
      );

      if (!response) {
        console.warn("No AI logs response from API");
        if (!append) setBursts([]);
        setLastAiLogsRefresh(new Date());
        setHasMore(false);
        setNextCursor(null);
        return;
      }

      // Convert raw events to AgentEvent format for compatibility
      const convertedBursts = response.bursts.map((burst) => ({
        burstId: burst.burstId,
        burstStartTime: burst.burstStartTime,
        burstEndTime: burst.burstEndTime,
        eventCount: burst.eventCount,
        events: burst.events.map(convertRawEventToAgentEvent),
      }));

      setBursts((prev) => append ? sortBurstsDescending([...prev, ...convertedBursts]) : sortBurstsDescending(convertedBursts));
      setLastAiLogsRefresh(new Date());
      setHasMore(response.hasMore);
      setNextCursor(response.nextCursor);
      setAiLogsError(null);

      if (!append && options.fetchStats) {
        try {
          const stats = await getAgenticAiLogStats(userData.email, latestTenant, selectedTimeRange, isGlobal, {
            computerName: device?.name,
            computerId: device?.id,
            typeFilter: typeFilter === "all" ? undefined : typeFilter,
            agentFilter: agentFilter === "all" ? undefined : agentFilter,
            jsonFilter: jsonFilter || undefined,
            customStartDate: selectedTimeRange === "custom" ? customStartDate : undefined,
            customEndDate: selectedTimeRange === "custom" ? customEndDate : undefined,
          });
          setOverviewStats(stats ?? null);
        } catch (statsError) {
          console.warn("Failed to load AI log overview stats:", statsError);
        }
      }
    } catch (error) {
      console.error("Error fetching AI logs:", error);
      setAiLogsError("Failed to load AI logs.");
      if (!append) setBursts([]);
      setHasMore(false);
      setNextCursor(null);
    } finally {
      setAiLogsLoading(false);
      setAiLogsLoadingMore(false);
    }
  }, [userData?.email, latestTenant, device?.name, device?.id, typeFilter, agentFilter, jsonFilter, selectedTimeRange, customStartDate, customEndDate, isGlobal]);

  const resetAndFetch = useCallback((options: { fetchStats?: boolean; searchQuery?: string } = {}) => {
    setBursts([]);
    setNextCursor(null);
    setHasMore(true);
    fetchAgenticAiLogs(null, false, options);
  }, [fetchAgenticAiLogs]);

  const handleSearchSubmit = useCallback((query: string) => {
    committedSearchRef.current = query;
    resetAndFetch({ fetchStats: false, searchQuery: query });
  }, [resetAndFetch]);

  const handleSearchClear = useCallback(() => {
    committedSearchRef.current = "";
    resetAndFetch({ fetchStats: true, searchQuery: "" });
  }, [resetAndFetch]);

  // Helper function to convert raw events to AgentEvent format
  const convertRawEventToAgentEvent = (rawEvent: any): AgentEvent => {
    const resourceType = String(
      rawEvent.resourceType ||
      rawEvent.aiEvent ||
      rawEvent.eventType ||
      ""
    ).toLowerCase();

    let eventType: EventType = "process-launch";

    if (resourceType.includes("fileread")) {
      eventType = "file-read";
    } else if (resourceType.includes("filewrite")) {
      eventType = "file-write";
    } else if (resourceType.includes("filedelete")) {
      eventType = "file-delete";
    } else if (
      resourceType.includes("network") ||
      resourceType.includes("connect")
    ) {
      eventType = "network-connect";
    }

    const parsedEventJson = parseEventJsonPayload(
      rawEvent.eventJson
    );

    return {
      id:
        rawEvent.requestId ||
        rawEvent.id ||
        rawEvent._time ||
        crypto.randomUUID(),

      timestamp:
        rawEvent.timestamp ||
        rawEvent._time ||
        new Date().toISOString(),

      type: eventType,

      rawUser:
        rawEvent.computerUserEmail ||
        rawEvent.actor ||
        "unknown",

      computer:
        rawEvent.computerName ||
        device?.name ||
        "unknown-device",

      parentProcess:
        parsedEventJson?.Process ||
        rawEvent.resourceName ||
        "AI Process",

      commandLine:
        rawEvent.message ||
        parsedEventJson?.Process ||
        "",

      childProcess:
        parsedEventJson?.ProcessChain?.[0] ||
        "",

      destination:
        rawEvent.resourceName ||
        "",

      destinationIp:
        parsedEventJson?.RemoteAddress,

      port: parsedEventJson?.RemotePort
        ? Number(parsedEventJson.RemotePort)
        : undefined,

      filePath:
        parsedEventJson?.FileName ||
        rawEvent.resourceName,

      action:
        rawEvent.resourceType ||
        rawEvent.aiEvent,

      message:
        rawEvent.message ||
        "",

      rawEvent,

      eventJson:
        parsedEventJson || rawEvent.eventJson,
    };
  };

  useEffect(() => {
    setBursts([]);
    setNextCursor(null);
    setHasMore(true);
    fetchAgenticAiLogs(null, false, { fetchStats: true });
  }, [typeFilter, agentFilter, selectedTimeRange, customStartDate, customEndDate, jsonFilter, fetchAgenticAiLogs]);

  async function handleExport() {
    if (!userData?.email || !latestTenant) {
      return;
    }

    setIsExporting(true);
    try {
      const exportSearchQuery = [committedSearchRef.current, agentFilter !== "all" ? agentFilter : undefined]
        .filter(Boolean)
        .join(" ");

      const exportedBlob = await exportAgenticAiLogs(userData.email, latestTenant, selectedTimeRange, isGlobal, {
        computerName: device?.name,
        computerId: device?.id,
        searchQuery: exportSearchQuery || undefined,
        typeFilter: typeFilter === "all" ? undefined : typeFilter,
        agentFilter: agentFilter === "all" ? undefined : agentFilter,
        jsonFilter: jsonFilter || undefined,
        customStartDate: selectedTimeRange === "custom" ? customStartDate : undefined,
        customEndDate: selectedTimeRange === "custom" ? customEndDate : undefined,
      });

      if (!exportedBlob) {
        throw new Error("No export file returned");
      }

      const url = window.URL.createObjectURL(exportedBlob);
      const link = document.createElement("a");
      link.href = url;
      const fileNameFragment = selectedTimeRange === "custom" && customStartDate && customEndDate
        ? `${customStartDate}-${customEndDate}`
        : selectedTimeRange;
      link.setAttribute("download", `agentic-ai-logs-${fileNameFragment}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export agentic AI logs:", error);
      setAiLogsError("Unable to export AI logs. Please try again.");
    } finally {
      setIsExporting(false);
    }
  }

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = containerRef.current;
    if (!sentinel || !root || !hasMore || aiLogsLoading || aiLogsLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && nextCursor) {
          fetchAgenticAiLogs(nextCursor, true);
        }
      },
      { root, rootMargin: "100px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, aiLogsLoading, aiLogsLoadingMore, nextCursor, fetchAgenticAiLogs]);

  const filteredBursts = useMemo(() => {
    return sortBurstsDescending(bursts);
  }, [bursts]);

  const burstAgents = useMemo(() =>
    Array.from(new Set(bursts.flatMap(b => b.events.map(e => parseRawUser(e.rawUser).agent).filter(Boolean)))) as string[]
    , [bursts]);

  const agentOptions = useMemo(() => {
    return overviewStats?.agents ?? burstAgents;
  }, [overviewStats, burstAgents]);

  const stats = useMemo(() => {
    if (overviewStats) {
      const process = overviewStats.eventTypeDistribution?.["AI-Process-Event"] ?? 0;
      const network = overviewStats.eventTypeDistribution?.["AI-Network-Event"] ?? 0;
      const fileOps = (
        (overviewStats.eventTypeDistribution?.["AI-FileRead-Event"] ?? 0) +
        (overviewStats.eventTypeDistribution?.["AI-FileWrite-Event"] ?? 0) +
        (overviewStats.eventTypeDistribution?.["AI-FileDelete-Event"] ?? 0)
      );
      return {
        total: overviewStats.totalLogs ?? 0,
        process,
        network,
        fileOps,
      };
    }

    const totalEvents = bursts.reduce((sum, b) => sum + b.eventCount, 0);
    const process = bursts.reduce((sum, b) => sum + b.events.filter((e) => e.type === "process-launch").length, 0);
    const network = bursts.reduce((sum, b) => sum + b.events.filter((e) => e.type === "network-connect").length, 0);
    const fileOps = bursts.reduce((sum, b) => sum + b.events.filter((e) => ["file-read", "file-write", "file-delete"].includes(e.type)).length, 0);
    return { total: totalEvents, process, network, fileOps };
  }, [bursts, overviewStats]);

  return (
    <div ref={containerRef} className="p-6 space-y-5 overflow-auto h-full bg-white dark:bg-background">

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-foreground">Agentic Activity</h2>
            <Badge variant="secondary" className="text-xs">Audit</Badge>
            {aiLogsLoading && <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Updating…</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            AI agent behaviour on <span className="font-medium text-foreground">{device?.name || "Device Name"}</span>
            {lastAiLogsRefresh && <span className="ml-2 text-xs text-muted-foreground">(Last refresh: {lastAiLogsRefresh.toLocaleTimeString()})</span>}
          </p>
          {overviewStats && (overviewStats.timeRange?.start && overviewStats.timeRange?.end) && (
            <p className="text-xs text-muted-foreground mt-1">
              Activity from {new Date(overviewStats.timeRange.start).toLocaleString()} – {new Date(overviewStats.timeRange.end).toLocaleString()}
            </p>
          )}
          {aiLogsError && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">⚠ {aiLogsError}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchAgenticAiLogs(null, false, { fetchStats: true })} disabled={aiLogsLoading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${aiLogsLoading ? "animate-spin" : ""}`} />
            {aiLogsLoading ? "Refreshing…" : "Refresh"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={aiLogsLoading || isExporting}>
            <Download className="w-3.5 h-3.5 mr-1.5" />
            {isExporting ? "Exporting…" : "Export"}
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {overviewStats ? (
          <>
            {/* API Stats */}
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Total Events</p>
                  <p className="text-2xl font-bold mt-1 text-foreground">{overviewStats.totalLogs}</p>
                </div>
                <ActivitySquare className="w-5 h-5 text-foreground opacity-60" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Unique Agents</p>
                  <p className="text-2xl font-bold mt-1 text-slate-700 dark:text-slate-300">{overviewStats?.agents?.length ?? burstAgents.length ?? 0}</p>
                </div>
                <User className="w-5 h-5 text-slate-700 dark:text-slate-300 opacity-60" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Process Launches</p>
                  <p className="text-2xl font-bold mt-1 text-violet-600 dark:text-violet-400">
                    {overviewStats.eventTypeDistribution?.["AI-Process-Event"] ?? stats.process}
                  </p>
                </div>
                <Terminal className="w-5 h-5 text-violet-600 dark:text-violet-400 opacity-60" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Network Connects</p>
                  <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">
                    {overviewStats.eventTypeDistribution?.["AI-Network-Event"] ?? stats.network}
                  </p>
                </div>
                <Network className="w-5 h-5 text-blue-600 dark:text-blue-400 opacity-60" />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">File Operations</p>
                  <p className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-400">
                    {(
                      (overviewStats.eventTypeDistribution?.["AI-FileRead-Event"] ?? 0) +
                      (overviewStats.eventTypeDistribution?.["AI-FileWrite-Event"] ?? 0) +
                      (overviewStats.eventTypeDistribution?.["AI-FileDelete-Event"] ?? 0)
                    ) || stats.fileOps}
                  </p>
                </div>
                <FileText className="w-5 h-5 text-amber-600 dark:text-amber-400 opacity-60" />
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            {/* Fallback Burst Stats when API stats not available */}
            {[
              { label: "Total Events", value: stats.total, icon: ActivitySquare, color: "text-foreground" },
              { label: "Unique Agents", value: burstAgents.length, icon: User, color: "text-slate-700 dark:text-slate-300" },
              { label: "Processes", value: stats.process, icon: Terminal, color: "text-violet-600 dark:text-violet-400" },
              { label: "Network", value: stats.network, icon: Network, color: "text-blue-600 dark:text-blue-400" },
              { label: "Operations", value: stats.fileOps, icon: FileText, color: "text-amber-600 dark:text-amber-400" },
            ].map(({ label, value, icon: Icon, color }) => (
              <Card key={label}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</p>
                    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
                  </div>
                  <Icon className={`w-5 h-5 ${color} opacity-60`} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>

      {!isGlobal && <GlobalSummaryPanel bursts={filteredBursts} />}

      <div className="flex flex-wrap gap-2 items-center">
        <ActivitySearchInput
          onSubmit={handleSearchSubmit}
          onClear={handleSearchClear}
          loading={aiLogsLoading}
        />
        <Select value={selectedTimeRange} onValueChange={handleSelectedTimeRangeChange}>
          <SelectTrigger className="w-36 h-9 text-sm"><SelectValue placeholder="Time range" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="lw">Last 7 Days</SelectItem>
            <SelectItem value="lm">Last 30 Days</SelectItem>
            <SelectItem value="l3m">Last 90 Days</SelectItem>
            <SelectItem value="custom">Custom range</SelectItem>
          </SelectContent>
        </Select>
        {selectedTimeRange === "custom" && (
          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-xs text-muted-foreground">From</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
            />
            <label className="text-xs text-muted-foreground">To</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
            />
          </div>
        )}
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-36 h-9 text-sm"><SelectValue placeholder="All Agents" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {agentOptions.map((a) => <SelectItem key={a} value={a}>{formatAgentDisplayName(a)}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className={`relative flex items-center gap-1 rounded-md border border-border bg-muted/40 p-0.5 ${aiLogsLoading ? "opacity-70 pointer-events-none" : ""}`}>
          {aiLogsLoading && (
            <div className="absolute -top-6 right-0 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Filtering…</span>
            </div>
          )}
          {(
            [
              { value: "all", label: "All" },
              { value: "process", label: "Launch" },
              { value: "network", label: "Network" },
              { value: "file", label: "File I/O" }
            ]
          ).map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setTypeFilter(value)}
              disabled={aiLogsLoading}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${typeFilter === value
                ? "bg-blue-600 text-white"
                : "text-muted-foreground hover-elevate"
                }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {showJsonFilter && (
        <div className="flex flex-wrap gap-2 items-center mt-2">
          <Input
            placeholder="Filter eventJson payload…"
            value={jsonFilter}
            onChange={(e) => setJsonFilter(e.target.value)}
            className="min-w-[280px] h-9 text-sm"
          />
          <Button variant="ghost" size="sm" onClick={() => setJsonFilter("")}>Clear</Button>
        </div>
      )}

      <ActivityBurstList
        bursts={filteredBursts}
        isGlobal={isGlobal}
        aiLogsLoading={aiLogsLoading}
        aiLogsLoadingMore={aiLogsLoadingMore}
        hasMore={hasMore}
        sentinelRef={sentinelRef}
      />

    </div>
  );
}
